import React, { useState, useEffect, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Camera, RotateCcw, X, AlertTriangle } from 'lucide-react'; // Added AlertTriangle

const CameraCapture = ({ 
  onCapture, 
  onClose, 
  title = "Capture Photo",
  showCameraSelection = true,
  defaultFacingMode = 'environment' // 'environment' for back camera, 'user' for front
}) => {
  const webcamRef = useRef(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // MODIFICATION: Added state to handle camera errors
  const [cameraError, setCameraError] = useState(null);

  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  const detectMobile = () => {
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);
  };

  const getAvailableCameras = useCallback(async () => {
    try {
      setIsLoading(true);
      setCameraError(null); // Reset error on each attempt
      
      // Request camera permission to enumerate devices
      await navigator.mediaDevices.getUserMedia({ video: true });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        // MODIFICATION: Handle the case where no cameras are found
        throw new Error("No camera devices were found on this device.");
      }
      
      const sortedCameras = videoDevices.sort((a, b) => {
        const aIsBack = a.label.toLowerCase().includes('back') || a.label.toLowerCase().includes('rear');
        const bIsBack = b.label.toLowerCase().includes('back') || b.label.toLowerCase().includes('rear');
        if (aIsBack && !bIsBack) return -1;
        if (!aIsBack && bIsBack) return 1;
        return 0;
      });

      setCameras(sortedCameras);
      
      if (sortedCameras.length > 0) {
        let selectedCameraDevice = sortedCameras[0];
        if (defaultFacingMode === 'environment' && isMobile) {
          const backCamera = sortedCameras.find(cam => cam.label.toLowerCase().includes('back') || cam.label.toLowerCase().includes('rear'));
          selectedCameraDevice = backCamera || sortedCameras[0];
        } else if (defaultFacingMode === 'user') {
          const frontCamera = sortedCameras.find(cam => cam.label.toLowerCase().includes('front'));
          selectedCameraDevice = frontCamera || sortedCameras[0];
        }
        setSelectedCamera(selectedCameraDevice);
      }
    } catch (error) {
      // MODIFICATION: Catch errors and set a user-friendly message
      console.error('Error initializing camera:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setCameraError("Camera access was denied. Please enable camera permissions for this site in your browser settings.");
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setCameraError("No camera found. Please ensure a camera is connected and enabled.");
      } else {
        setCameraError(error.message || "An unexpected error occurred while accessing the camera.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [defaultFacingMode, isMobile]);

  useEffect(() => {
    detectMobile();
    getAvailableCameras();
  }, [getAvailableCameras]);

  const switchCamera = () => {
    if (cameras.length > 1) {
      const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCamera?.deviceId);
      const nextIndex = (currentIndex + 1) % cameras.length;
      setSelectedCamera(cameras[nextIndex]);
    }
  };
  
  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (onCapture) {
        onCapture(imageSrc);
      }
    }
  };

  const getVideoConstraints = () => {
    const constraints = {
      width: { ideal: 1280 },
      height: { ideal: 720 }
    };
    if (selectedCamera?.deviceId) {
      constraints.deviceId = { exact: selectedCamera.deviceId };
    } else {
      constraints.facingMode = defaultFacingMode;
    }
    return constraints;
  };
  
  // All touch and zoom handlers remain unchanged
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      setLastTouchDistance(distance);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setLastPanPoint({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };
  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      const distance = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      if (lastTouchDistance > 0) {
        const newScale = scale * (distance / lastTouchDistance);
        setScale(Math.min(Math.max(newScale, 0.5), 3));
      }
      setLastTouchDistance(distance);
    } else if (e.touches.length === 1 && isDragging) {
      const deltaX = e.touches[0].clientX - lastPanPoint.x;
      const deltaY = e.touches[0].clientY - lastPanPoint.y;
      setPanX(prev => prev + deltaX);
      setPanY(prev => prev + deltaY);
      setLastPanPoint({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };
  const handleTouchEnd = () => {
    setIsDragging(false);
    setLastTouchDistance(0);
  };
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = scale * delta;
    setScale(Math.min(Math.max(newScale, 0.5), 3));
  };
  const resetView = () => {
    setScale(1);
    setPanX(0);
    setPanY(0);
  };
  const getTransformStyle = () => ({
    transform: `scale(${scale}) translate(${panX}px, ${panY}px)`,
    transformOrigin: 'center',
    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl border border-gray-100">
          <div className="spinner mx-auto mb-6"></div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Initializing Camera</h3>
          <p className="text-gray-600">Please wait and allow camera access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-2xl p-4 w-full h-full max-w-md max-h-[90vh] flex flex-col relative shadow-2xl border border-gray-100">
        <div className="flex justify-between items-center mb-4 flex-shrink-0 pb-3 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <Camera className="h-6 w-6 mr-2 text-blue-600" />
            {title}
          </h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* MODIFICATION: Conditionally render error message or camera view */}
        {cameraError ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <AlertTriangle className="w-16 h-16 text-red-400 mb-4" />
            <h4 className="text-lg font-bold text-red-600 mb-2">Camera Error</h4>
            <p className="text-gray-700">{cameraError}</p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="relative flex-1 min-h-0 mb-4 overflow-hidden rounded-xl border-2 border-gray-100 shadow-lg">
              <div
                className="w-full h-full"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
                style={getTransformStyle()}
              >
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="w-full h-full object-cover"
                  videoConstraints={getVideoConstraints()}
                  onUserMedia={() => setIsLoading(false)}
                  onUserMediaError={(error) => {
                    console.error('onUserMediaError:', error);
                    setCameraError("Could not start camera. Please ensure it is not in use by another application.");
                  }}
                />
              </div>
              {scale !== 1 && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white px-3 py-2 rounded-full text-sm font-medium shadow-lg">
                  📷 {Math.round(scale * 100)}%
                </div>
              )}
              {scale !== 1 && (
                <button onClick={resetView} className="absolute top-3 right-3 bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 rounded-full shadow-lg" title="Reset Zoom">
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex justify-center space-x-4 flex-shrink-0">
              <button type="button" onClick={onClose} className="p-4 bg-gray-600 text-white rounded-full shadow-lg" title="Cancel">
                <X className="h-6 w-6" />
              </button>
              {showCameraSelection && cameras.length > 1 && (
                <button type="button" onClick={switchCamera} className="p-4 bg-blue-600 text-white rounded-full shadow-lg" title="Switch Camera">
                  <RotateCcw className="h-6 w-6" />
                </button>
              )}
              <button type="button" onClick={capturePhoto} className="p-4 bg-emerald-600 text-white rounded-full shadow-lg" title="Capture Photo">
                <Camera className="h-6 w-6" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;